/**
 * RecipeDetailPanel — queue #664 §UAT-2026-07-09-R664 §R664-3/§R664-4.
 *
 * 레시피 선택 시 상세 — 구성 모듈(모듈 상세 딥링크) · 단계별 wiring(스니펫 코드블록 +
 * CopyButton) · 합성 yml(`mergeRecipeYml` 파생 · 비면 "추가 opt-in 설정 불요" 안내) · 관련 데모
 * 딥링크 · "레시피 통째로 담기"(§R641 `POST /catalog/module-source-bundles` 재사용 · 신규 BE 0).
 *
 * 5상태: default(본문) · loading(담기 POST 진행 중 버튼 spinner+disabled) · empty(합성 yml
 * 전체 core → 안내 문구) · error(담기 실패 인라인 ErrorState) · dialog(담기 전 "포함될 N 모듈
 * 확인" 프리뷰 — §R641-5 CurationBundlePicker confirm 패턴 재사용).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CopyButton, FormDialog, ErrorState } from '@ui-kit';
import type { Recipe } from '../data/recipes';
import { mergeRecipeYml, recipeModuleCodes } from '../data/recipes';
import { useRecipeBundle } from '../hooks/useRecipeBundle';
import { showcaseApi } from '../api/showcaseApi';
import takeThisJson from '../data/take-this.json';
import type { ApiError, TakeThisData } from '../types/showcase';

const TAKE_THIS_DATA = takeThisJson as unknown as TakeThisData;

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const blockStyle: React.CSSProperties = {
  marginBottom: 'var(--spacing-4)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'var(--color-surface-elevated)',
};

const blockHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-2)',
};

const blockTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: 0,
};

const preStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  background: 'var(--color-surface-muted)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--spacing-3)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.6,
  margin: 0,
};

interface Props {
  recipe: Recipe;
  onBack: () => void;
  /** 구성 모듈 배지 또는 '/showcase#<code>' 관련 데모 클릭 시 — 카탈로그 뷰로 전환 + 해당 노드 선택 */
  onSelectModule: (code: string) => void;
}

export function RecipeDetailPanel({ recipe, onBack, onSelectModule }: Props) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { mutate, isPending, error, isError, reset } = useRecipeBundle();

  const selectedCodes = useMemo(() => recipe.modules.map((m) => m.code), [recipe]);
  // §R641-2 계보: 전이 SSOT = take-this.json deps.codes — FE 재계산 금지.
  const totalCodes = useMemo(() => recipeModuleCodes(recipe, TAKE_THIS_DATA), [recipe]);
  const transitiveCount = totalCodes.length - selectedCodes.length;

  const ymlLines = useMemo(() => mergeRecipeYml(recipe, TAKE_THIS_DATA), [recipe]);
  const ymlText = ymlLines.map((l) => l.text).join('\n');

  function handleTakeAllClick() {
    setDialogOpen(true);
  }

  function handleConfirmDownload() {
    setDialogOpen(false);
    reset();
    mutate(totalCodes, {
      onSuccess: ({ blob, filename }) => {
        downloadBlob(blob, filename);
        // queue #678 §R678-4③: 레시피 통째로 담기 → TAKE 시그널(모듈 전체 · fire-and-forget)
        void showcaseApi.recordSignal('TAKE', totalCodes).catch(() => {});
      },
    });
  }

  const apiError = error as ApiError | null;
  const statusGuidance =
    apiError?.statusCode === 429
      ? t('showcase.recipe.detail.error.rateLimited')
      : apiError?.statusCode === 502
      ? t('showcase.recipe.detail.error.unavailable')
      : apiError?.statusCode === 404
      ? t('showcase.recipe.detail.error.generic')
      : null;

  return (
    <section aria-label={t(recipe.nameKey)}>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-brand-primary)',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          padding: 0,
          marginBottom: 'var(--spacing-3)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        {t('showcase.recipe.detail.back')}
      </button>

      <h2
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-1) 0',
        }}
      >
        {t(recipe.nameKey)}
      </h2>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          margin: '0 0 var(--spacing-4) 0',
        }}
      >
        {t(recipe.summaryKey)}
      </p>

      {/* 구성 모듈 */}
      <div style={blockStyle}>
        <p style={blockTitleStyle}>{t('showcase.recipe.detail.modules')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)' }}>
          {recipe.modules.map((m) => (
            <button
              key={m.code}
              type="button"
              onClick={() => onSelectModule(m.code)}
              aria-label={t('showcase.recipe.detail.moduleJumpAria', { code: m.code })}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
                fontSize: 'var(--font-size-xs)',
                background: 'var(--color-surface-background)',
                border: '1px solid var(--color-brand-primary)',
                color: 'var(--color-brand-primary)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px var(--spacing-2)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              <code style={{ fontFamily: 'var(--font-family-mono)' }}>{m.code}</code>
              <span>{t(m.roleKey)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 단계별 wiring */}
      <div style={blockStyle}>
        <p style={blockTitleStyle}>{t('showcase.recipe.detail.steps')}</p>
        <ol style={{ margin: 'var(--spacing-2) 0 0', paddingLeft: 'var(--spacing-4)' }}>
          {recipe.steps.map((step, idx) => (
            <li key={idx} style={{ marginBottom: 'var(--spacing-3)' }}>
              <div style={blockHeaderStyle}>
                <p style={{ ...blockTitleStyle, fontSize: 'var(--font-size-xs)' }}>{t(step.titleKey)}</p>
                <CopyButton
                  text={step.snippet}
                  ariaLabel={t('showcase.takeThis.copyAria', { block: t(step.titleKey) })}
                  copyLabel={t('showcase.takeThis.copy')}
                  copiedLabel={t('showcase.takeThis.copied')}
                />
              </div>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', margin: '0 0 var(--spacing-2) 0' }}>
                {t(step.descKey)}
              </p>
              <pre style={preStyle}>{step.snippet}</pre>
            </li>
          ))}
        </ol>
      </div>

      {/* 합성 yml */}
      <div style={blockStyle} data-testid="recipe-yml-block">
        <div style={blockHeaderStyle}>
          <p style={blockTitleStyle}>{t('showcase.recipe.detail.yml.title')}</p>
          {ymlLines.length > 0 && (
            <CopyButton
              text={ymlText}
              ariaLabel={t('showcase.takeThis.copyAria', { block: t('showcase.recipe.detail.yml.title') })}
              copyLabel={t('showcase.takeThis.copy')}
              copiedLabel={t('showcase.takeThis.copied')}
            />
          )}
        </div>
        {ymlLines.length === 0 ? (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic', margin: 0 }}>
            {t('showcase.recipe.detail.yml.empty')}
          </p>
        ) : (
          <pre style={preStyle}>{ymlText}</pre>
        )}
      </div>

      {/* 관련 데모 */}
      <div style={blockStyle}>
        <p style={blockTitleStyle}>{t('showcase.recipe.detail.demos')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)' }}>
          {recipe.demoRoutes.map((route) =>
            route.startsWith('/showcase#') ? (
              <button
                key={route}
                type="button"
                onClick={() => onSelectModule(route.replace('/showcase#', ''))}
                aria-label={t('showcase.recipe.detail.demoLinkAria', { route })}
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  background: 'var(--color-surface-background)',
                  border: '1px solid var(--color-surface-border)',
                  color: 'var(--color-brand-primary)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '2px var(--spacing-2)',
                  cursor: 'pointer',
                }}
              >
                {route}
              </button>
            ) : (
              <Link
                key={route}
                to={route}
                aria-label={t('showcase.recipe.detail.demoLinkAria', { route })}
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  border: '1px solid var(--color-surface-border)',
                  color: 'var(--color-brand-primary)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '2px var(--spacing-2)',
                  textDecoration: 'none',
                }}
              >
                {route}
              </Link>
            )
          )}
        </div>
      </div>

      {/* 레시피 통째로 담기 */}
      <button
        type="button"
        onClick={handleTakeAllClick}
        disabled={isPending}
        aria-busy={isPending}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-4)',
          background: isPending ? 'var(--color-surface-elevated)' : 'var(--color-brand-primary)',
          color: isPending ? 'var(--color-text-secondary)' : 'var(--color-text-inverse)',
          border: isPending ? '1px solid var(--color-surface-border)' : 'none',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          cursor: isPending ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        {isPending && (
          <span
            aria-hidden="true"
            style={{
              width: 12,
              height: 12,
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: 'var(--color-text-inverse)',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        )}
        {isPending ? t('showcase.recipe.detail.downloading') : t('showcase.recipe.detail.takeAll')}
      </button>

      {isError && apiError && (
        <div style={{ marginTop: 'var(--spacing-3)' }}>
          <ErrorState
            title={apiError.code || t('showcase.recipe.detail.error.generic')}
            message={statusGuidance ? `${apiError.message} ${statusGuidance}` : apiError.message}
            onRetry={handleTakeAllClick}
          />
        </div>
      )}

      <FormDialog
        open={dialogOpen}
        title={t('showcase.recipe.detail.confirm.title')}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleConfirmDownload}
        submitLabel={t('showcase.recipe.detail.download')}
        cancelLabel={t('showcase.recipe.detail.cancel')}
        submitTestId="recipe-bundle-confirm-download"
      >
        <p aria-live="polite" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: 0 }}>
          {t('showcase.recipe.detail.confirm.count', {
            selected: selectedCodes.length,
            transitive: transitiveCount,
            total: totalCodes.length,
          })}
        </p>
        <ul style={{ margin: 0, paddingLeft: 'var(--spacing-4)' }}>
          {totalCodes.map((code) => (
            <li key={code} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
              <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}>{code}</code>
            </li>
          ))}
        </ul>
      </FormDialog>
    </section>
  );
}
